import type {
  AccountingKpis,
  DailyDashboardKpi,
  DailyStockKpi,
  DayOfWeek,
  HoursData,
  NetworkDashboardKpis,
  NetworkDashboardLiveStoreCa,
  NetworkDashboardStoreAgeFilter,
  TimeSpentData,
} from '@gozoki/api-types'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import {
  type FetchNetworkDashboardKpisParams,
  fetchAccountingKpis,
  fetchAccountingWarnings,
  fetchAllDailysKpis,
  fetchAllStockKpis,
  fetchHoursData,
  fetchNetworkDashboardKpis,
  fetchNetworkDashboardLiveStoreCa,
  fetchTimeSpentData,
} from '../queries/kpi.queries'

export const useStoresKpis = (): UseQueryResult<DailyDashboardKpi[], unknown> =>
  useQuery({
    queryKey: ['get-kpi'],
    queryFn: () => fetchAllDailysKpis(),
  })

export const useNetworkDashboardKpis = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
): UseQueryResult<NetworkDashboardKpis, unknown> =>
  useQuery({
    queryKey: [
      'get-network-dashboard-kpis',
      storeIds,
      storeAgeFilter,
      period?.ageStoreIds,
      period?.startDate,
      period?.endDate,
      period?.cohortUnit,
      period?.cohortValue,
    ],
    queryFn: () => fetchNetworkDashboardKpis(storeIds, storeAgeFilter, period),
  })

export const useNetworkDashboardLiveStoreCa = (): UseQueryResult<
  NetworkDashboardLiveStoreCa,
  unknown
> =>
  useQuery({
    queryKey: ['get-network-dashboard-live-store-ca'],
    queryFn: () => fetchNetworkDashboardLiveStoreCa(),
    refetchInterval: 300,
  })

export const useStockKpis = (): UseQueryResult<DailyStockKpi[], unknown> =>
  useQuery({
    queryKey: ['get-stock-kpi'],
    queryFn: () => fetchAllStockKpis(),
  })

export const useAccountingKpis = (date: string): UseQueryResult<AccountingKpis, unknown> =>
  useQuery({
    queryKey: ['get-accounting-kpis', date],
    queryFn: () => fetchAccountingKpis(date),
  })

export const useAccountingWarnings = (): UseQueryResult<
  { date: string; difference: number }[],
  unknown
> =>
  useQuery({
    queryKey: ['get-accounting-warnings'],
    queryFn: () => fetchAccountingWarnings(),
  })

export const useHoursData = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
): UseQueryResult<HoursData[], unknown> => {
  const plageKey = plage || 'DAY'

  return useQuery({
    queryFn: () => fetchHoursData(days, storeIds, plage, dateStart, dateEnd),
    queryKey: ['get-hours-data', days, storeIds, plageKey, dateStart, dateEnd],
  })
}

export const useTimeSpentDatas = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
): UseQueryResult<TimeSpentData[], unknown> => {
  const plageKey = plage || 'day'
  return useQuery({
    queryFn: () => fetchTimeSpentData(days, storeIds, plage, dateEnd, dateStart),
    queryKey: ['get-time-spent-data', days, storeIds, plageKey, dateStart, dateEnd],
  })
}
